import { type RefObject, useEffect } from 'react';

// Keeps the body table's horizontal scroll position and the sticky header
// wrapper's scroll position mirrored. The header wrapper renders a sibling
// `<table>` outside the body's `overflow-x-auto` ancestor so that
// `position: sticky` can attach to the page's scroll container; in exchange
// we drive its `scrollLeft` programmatically here.
//
// Both directions are wired so the user can pan horizontally by scrolling
// either the header or the body. Programmatic writes to one side trigger that
// side's own `scroll` event, which would cause an infinite ping-pong; the
// `isSyncing` guard breaks the cycle by skipping the next event after a write.
const useDataTableStickyHeaderScrollSync = (
  bodyScrollWrapperRef: RefObject<HTMLDivElement | null>,
  headerWrapperRef: RefObject<HTMLDivElement | null>,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const bodyWrapper = bodyScrollWrapperRef.current;
    const headerWrapper = headerWrapperRef.current;

    if (!bodyWrapper || !headerWrapper) {
      return;
    }

    const bodyScrollContainer = bodyWrapper.querySelector<HTMLElement>(
      '[data-slot="table-container"]',
    );

    if (!bodyScrollContainer) {
      return;
    }

    let isSyncing = false;

    const sync = (source: HTMLElement, target: HTMLElement) => {
      if (isSyncing) {
        isSyncing = false;
        return;
      }

      if (target.scrollLeft === source.scrollLeft) {
        return;
      }

      isSyncing = true;
      target.scrollLeft = source.scrollLeft;
    };

    const onBodyScroll = () => sync(bodyScrollContainer, headerWrapper);
    const onHeaderScroll = () => sync(headerWrapper, bodyScrollContainer);

    headerWrapper.scrollLeft = bodyScrollContainer.scrollLeft;

    bodyScrollContainer.addEventListener('scroll', onBodyScroll, { passive: true });
    headerWrapper.addEventListener('scroll', onHeaderScroll, { passive: true });

    return () => {
      bodyScrollContainer.removeEventListener('scroll', onBodyScroll);
      headerWrapper.removeEventListener('scroll', onHeaderScroll);
    };
  }, [bodyScrollWrapperRef, headerWrapperRef, enabled]);
};

export default useDataTableStickyHeaderScrollSync;
